import { logger } from '~/utils/logger';

import { decrypt, encrypt, getEncryptionKeyFromString } from './encryption';

interface EncryptedStorageItem<T> {
  key: string;
  expiresAt: number;
  value: T;
}

const EXPIRATION_TIME = 1000 * 60 * 60 * 24; // 24 hours

class StorageServiceClass {
  private cryptoKey: CryptoKey | null = null;
  private isReadyResolve: (() => void) | null = null;
  private isReadyPromise = new Promise<void>((resolve) => {
    this.isReadyResolve = resolve;
  });

  isReady = () => {
    return this.isReadyPromise;
  };

  tryCatchWrap = (callback: () => null | string | void) => {
    try {
      return callback();
    } catch (error) {
      logger.storage('Error with unsecured storage operation:', error);
      return null;
    }
  };

  getItem = (key: string) => this.tryCatchWrap(() => localStorage.getItem(key));

  setItem = (key: string, value: string) =>
    this.tryCatchWrap(() => localStorage.setItem(key, value));

  removeItem = (key: string) =>
    this.tryCatchWrap(() => localStorage.removeItem(key));

  setEncryptionKey = async (encryptionKey: string) => {
    this.cryptoKey = await getEncryptionKeyFromString(encryptionKey);
    this.isReadyResolve?.();
  };

  private getEncryptedItem = async <T>(key: string) => {
    try {
      const encryptedValue = localStorage.getItem(key) ?? '';
      const decrypted = (await decrypt(encryptedValue, this.cryptoKey)) ?? '';
      const parsed = JSON.parse(decrypted);

      if (parsed.expiresAt < Date.now()) {
        return null;
      }

      return parsed.value as T;
    } catch (error) {
      logger.storage('Error getting encrypted storage item:', error);
      return null;
    }
  };

  private setEncryptedItem = async <T>(
    key: string,
    item: EncryptedStorageItem<T>,
  ) => {
    try {
      const encrypted = await encrypt(JSON.stringify(item), this.cryptoKey);
      if (encrypted) {
        localStorage.setItem(key, encrypted);
      }
    } catch (error) {
      logger.storage('Error storing encrypted item:', error);
    }
  };

  getSecureItem = async <T>(key: string) => {
    return this.getEncryptedItem<T>(key);
  };

  setSecureItem = async <T>(key: string, value: T) => {
    return this.setEncryptedItem(key, {
      key,
      expiresAt: Date.now() + EXPIRATION_TIME,
      value,
    });
  };
}

export const StorageService = new StorageServiceClass();
